import { createEventHook, createGlobalState } from '@vueuse/core'
import { readonly, shallowRef } from 'vue'
import type { CredentialKind } from '~/models/terminalCredentialStatus'
import type { TerminalName } from '~/services/apiClient'

/** What made the prompt appear, for the usage events. */
export type CredentialPromptTrigger =
  'upload' | 'refresh' | 'slot_lookup' | 'booking_dialog' | 'booking_submit'

export interface CredentialGapRequest {
  kind: CredentialKind
  terminals: TerminalName[]
  trigger: CredentialPromptTrigger
}

/** What a prompt closed after a fix was about. */
export interface CredentialGapFixed {
  kind: CredentialKind
  terminals: TerminalName[]
}

function useCredentialGapDialogState() {
  const current = shallowRef<CredentialGapRequest | null>(null)
  const fixedHook = createEventHook<CredentialGapFixed>()

  /**
   * Asks for the prompt. This only records the wish: whether it can show right
   * now (another modal, an ineligible user) is `GlobalCredentialGapDialog`'s call.
   *
   * A request of the same kind joins the one already pending, so containers that
   * resolve a few seconds apart end up in one dialog. A different kind replaces
   * it, since the newer one is what the user just did.
   */
  function request(next: CredentialGapRequest) {
    if (next.terminals.length === 0) return
    const pending = current.value
    if (pending?.kind === next.kind) {
      current.value = {
        ...pending,
        terminals: [...new Set([...pending.terminals, ...next.terminals])],
      }
      return
    }
    current.value = { ...next, terminals: [...new Set(next.terminals)] }
  }

  function close() {
    current.value = null
  }

  /**
   * Closes a prompt whose gaps were all fixed and says so. This is how whatever
   * asked for the prompt (a failed slot lookup, say) learns it can try again;
   * a plain `close()` means the user moved on and nothing was fixed.
   */
  function closeAfterFix(fixed: CredentialGapFixed) {
    close()
    return fixedHook.trigger(fixed)
  }

  return {
    current: readonly(current),
    request,
    close,
    closeAfterFix,
    // Unsubscribes by itself when the calling component or scope goes away
    onFixed: fixedHook.on,
  }
}

export const useCredentialGapDialog = createGlobalState(
  useCredentialGapDialogState
)
